import type { LiveGambitDto } from '@/core/data/gambit/gambit.interface';
import type { LiveHandshakeDto } from '@/core/data/handshake/handshake.interface';
import { gambits } from '@/core/gambits';
import { useAuthStore } from '@/stores/auth.store';
import { useGambitStore } from '@/stores/gambit.store';
import { useLiveHandshakesStore } from '@/stores/liveHandshakes.store';
import { useNavigationStore, type ActiveCheckIn } from '@/stores/navigation.store';
import { liveBannerItems, type LiveBannerItem } from '@/views/components/gam-live-banner/live-banner-items';
import { GamStoreId } from '@/views/composables/constants/main/store.constants';
import { useNow } from '@vueuse/core';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';

const REFRESH_MS = 60_000;
/** Navigating around reloads at most this often, so moving between screens stays cheap. */
const NAVIGATION_REFRESH_MS = 15_000;

/**
 * Everything live for the signed-in player, one row each: the gambit to check
 * into or checked into, the spot checked into, the open handshakes, and the
 * gambits that ended in the last day.
 */
export const useLiveBannerStore = defineStore(GamStoreId.LIVE_BANNER, () => {
  const { user } = storeToRefs(useAuthStore());
  const navigationStore = useNavigationStore();
  const { activeCheckIn, isCheckedIn } = storeToRefs(navigationStore);
  const handshakesStore = useLiveHandshakesStore();
  const gambitStore = useGambitStore();

  const now = useNow({ interval: 30_000 });

  const liveGambits = ref<LiveGambitDto[]>([]);

  let lastLoadedAt = 0;
  let refreshTimer: ReturnType<typeof setInterval> | null = null;

  const load = async () => {
    if (!user.value?.id) return;

    lastLoadedAt = Date.now();
    const result = await gambits.gambitService.getLiveGambits();
    if (result.data) liveGambits.value = result.data;
  };

  const loadIfStale = (maxAgeMs = NAVIGATION_REFRESH_MS) => {
    if (Date.now() - lastLoadedAt < maxAgeMs) return;
    void load();
  };

  /* A woken phone hears the handshake listener only once Firestore reconnects, so ask for the handshakes at once too. */
  const onVisible = () => {
    if (document.visibilityState !== 'visible') return;
    loadIfStale();
    void handshakesStore.load();
  };

  const start = () => {
    stop();
    void load();
    void handshakesStore.ensure();
    // A backgrounded app catches up through `onVisible` instead.
    refreshTimer = setInterval(() => {
      if (document.visibilityState === 'visible') loadIfStale(REFRESH_MS);
    }, REFRESH_MS);
    document.addEventListener('visibilitychange', onVisible);
  };

  const stop = () => {
    if (refreshTimer) clearInterval(refreshTimer);
    refreshTimer = null;
    document.removeEventListener('visibilitychange', onVisible);
    liveGambits.value = [];
    lastLoadedAt = 0;
  };

  watch(
    () => user.value?.id ?? null,
    (userId) => (userId ? start() : stop()),
  );

  // Checking in or out anywhere moves a gambit from one row state to the next.
  watch(
    () => activeCheckIn.value?.checkedInAt ?? null,
    () => void load(),
  );

  const spotCheckIn = computed<ActiveCheckIn | null>(() => (isCheckedIn.value ? activeCheckIn.value : null));

  const items = computed<LiveBannerItem[]>(() =>
    liveBannerItems({
      gambits: liveGambits.value,
      spotCheckIn: spotCheckIn.value,
      handshakes: handshakesStore.handshakes,
      now: now.value,
    }),
  );

  /** Checks into the gambit where the row is, without leaving the screen. Resolves to whether it worked. */
  const checkIn = async (gambit: LiveGambitDto): Promise<boolean> => {
    const location = gambit.location;
    if (!location) return false;

    const result = await gambits.locationService.checkIn(location.id, { gambitId: gambit.id });
    if (!result?.data) return false;

    gambits.locationService.updateMarkerCheckInStatus(location.id, true);
    // Reloads the banner through the check-in watcher.
    navigationStore.setActiveCheckIn({
      locationId: location.id,
      locationName: location.name,
      coordinates: location.coordinates,
      gambitId: gambit.id,
      checkedInAt: result.data.start,
    });

    if (gambitStore.gambit?.id === gambit.id) await gambitStore.fetch(gambit.id);
    return true;
  };

  const checkOut = async (locationId: string, gambitId?: string | null) => {
    await gambits.locationService.checkOut(locationId);
    gambits.locationService.updateMarkerCheckInStatus(locationId, false);
    if (activeCheckIn.value?.locationId === locationId) navigationStore.clearActiveCheckIn();

    if (gambitId && gambitStore.gambit?.id === gambitId) {
      await gambitStore.fetch(gambitId);
    }

    await load();
  };

  /** Off the banner at once. The server keeps it off on every other phone. */
  const dismissRecent = async (gambitId: string) => {
    liveGambits.value = liveGambits.value.filter((gambit) => gambit.id !== gambitId);
    const result = await gambits.gambitService.dismissRecentGambit(gambitId);
    if (result.error) await load();
    return result;
  };

  const closeHandshake = (handshake: LiveHandshakeDto) => handshakesStore.close(handshake.id);

  return {
    items,
    load,
    loadIfStale,
    start,
    stop,
    checkIn,
    checkOut,
    dismissRecent,
    closeHandshake,
  };
});
